import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ExternalLink, Trash2 } from 'lucide-react';
import { adminApi, jobsApi } from '../../services/api';
import { Skeleton } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { formatDate, formatCurrency, getStatusColor, getStatusLabel } from '../../utils/helpers';
import type { Job, PaginatedResponse } from '../../types';

const statusFilters = [
  { value: 'open', label: 'Abierto' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'completed', label: 'Completado' },
  { value: 'cancelled', label: 'Cancelado' },
];

export const AdminJobs: React.FC = () => {
  const [result, setResult] = useState<PaginatedResponse<Job> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    adminApi
      .jobs({ search: search || undefined, status: status || undefined, page })
      .then((res) => setResult(res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const handleDelete = async (job: Job) => {
    if (!window.confirm(`¿Eliminar el trabajo "${job.title}"? Esta acción no se puede deshacer.`)) return;
    setDeletingId(job.id);
    try {
      await jobsApi.delete(job.id);
      setResult((prev) => (prev ? { ...prev, data: prev.data.filter((j) => j.id !== job.id) } : prev));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-onSurface">Trabajos</h1>
        <p className="text-onSurface-mute">Moderá los trabajos publicados en la plataforma</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-onSurface-mute" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título..."
            className="w-full pl-9 pr-4 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface placeholder-onSurface-mute focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </form>

        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
          className="px-3 py-2 text-sm rounded-xl border border-outlineVarWarm bg-surface text-onSurface focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          <option value="">Todos los estados</option>
          {statusFilters.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-surface rounded-2xl border border-outlineVarWarm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : result && result.data.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-outlineVarWarm text-left text-onSurface-mute">
                <th className="px-5 py-3 font-medium">Trabajo</th>
                <th className="px-5 py-3 font-medium">Cliente</th>
                <th className="px-5 py-3 font-medium">Categoría</th>
                <th className="px-5 py-3 font-medium">Estado</th>
                <th className="px-5 py-3 font-medium">Presupuesto</th>
                <th className="px-5 py-3 font-medium">Fecha</th>
                <th className="px-5 py-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outlineVarWarm">
              {result.data.map((job) => (
                <tr key={job.id}>
                  <td className="px-5 py-3 font-medium text-onSurface max-w-xs truncate">{job.title}</td>
                  <td className="px-5 py-3 text-onSurface-var">{job.client?.full_name || '—'}</td>
                  <td className="px-5 py-3 text-onSurface-var">{job.category?.name || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`badge ${getStatusColor(job.status)}`}>{getStatusLabel(job.status)}</span>
                  </td>
                  <td className="px-5 py-3 text-onSurface-var">
                    {job.budget_range || formatCurrency(job.budget_max)}
                  </td>
                  <td className="px-5 py-3 text-onSurface-var">{formatDate(job.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <Link
                        to={`/jobs/${job.id}`}
                        target="_blank"
                        className="p-2 rounded-lg text-onSurface-var hover:bg-surface-var transition-colors"
                        title="Ver trabajo"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDelete(job)}
                        disabled={deletingId === job.id}
                        className="p-2 rounded-lg text-danger-600 hover:bg-danger-50 transition-colors disabled:opacity-50"
                        title="Eliminar trabajo"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-onSurface-mute text-center py-12">No se encontraron trabajos</p>
        )}
      </div>

      {result && <Pagination meta={result} onPageChange={setPage} />}
    </div>
  );
};
